import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  Plus,
  Copy,
  Trash2,
  Eye,
  Edit3,
  Code,
  FileCode,
  Search,
  Send,
  Tag
} from 'lucide-react';

function extractMergeTags(html: string): string[] {
  const matches = html.match(/\{\{(\w+)\}\}/g) || [];
  const seen = new Set<string>();
  return matches.map(m => m.replace(/\{\{|\}\}/g, '')).filter(t => seen.has(t) ? false : seen.add(t) && true);
}

const templateSchema = z.object({
  name: z.string().min(1, 'Template name is required'),
  subject: z.string().min(1, 'Email subject line is required'),
  htmlContent: z.string().min(1, 'HTML template body is required'),
  textContent: z.string().optional()
});

type TemplateFormValues = z.infer<typeof templateSchema>;

export const Templates: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  
  const [search, setSearch] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // Preview states
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<{ subject: string; htmlContent: string } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Fetch templates
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      const res = await apiClient.get('/templates');
      return res.data;
    }
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema)
  });

  // Create or Update Template Mutation
  const saveMutation = useMutation({
    mutationFn: async (data: TemplateFormValues) => {
      if (isEditMode && editingTemplateId) {
        const res = await apiClient.put(`/templates/${editingTemplateId}`, data);
        return res.data;
      } else {
        const res = await apiClient.post('/templates', data);
        return res.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['templates']);
      showToast(
        isEditMode ? 'Template updated successfully' : 'Template created successfully',
        'success'
      );
      setIsModalOpen(false);
      reset();
      setIsEditMode(false);
      setEditingTemplateId(null);
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to save template';
      showToast(msg, 'error');
    }
  });

  // Clone Mutation
  const cloneMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/templates/${id}/clone`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['templates']);
      showToast('Template duplicated successfully', 'success');
    },
    onError: () => {
      showToast('Failed to duplicate template', 'error');
    }
  });

  // Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/templates/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['templates']);
      showToast('Template deleted successfully', 'success');
      setDeleteTargetId(null);
    },
    onError: () => {
      showToast('Failed to delete template', 'error');
      setDeleteTargetId(null);
    }
  });

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return templates;
    const q = search.toLowerCase();
    return templates.filter((t: any) =>
      t.name?.toLowerCase().includes(q) || t.subject?.toLowerCase().includes(q)
    );
  }, [templates, search]);

  const handleEditClick = (template: any) => {
    setIsEditMode(true);
    setEditingTemplateId(template.id);
    setValue('name', template.name);
    setValue('subject', template.subject);
    setValue('htmlContent', template.htmlContent);
    setValue('textContent', template.textContent || '');
    setIsModalOpen(true);
  };

  const handlePreviewClick = async (id: string) => {
    setIsPreviewOpen(true);
    setLoadingPreview(true);
    try {
      const res = await apiClient.get(`/templates/${id}/preview`);
      setPreviewData(res.data);
    } catch (err) {
      showToast('Failed to load template preview', 'error');
      setIsPreviewOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const onSubmit = (data: TemplateFormValues) => {
    saveMutation.mutate(data);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight uppercase">Email Templates</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {templates.length} template{templates.length !== 1 ? 's' : ''} · Supports {"{{firstName}}"} merge tags
          </p>
        </div>
        <button
          onClick={() => { setIsEditMode(false); setEditingTemplateId(null); reset(); setIsModalOpen(true); }}
          className="btn-primary flex items-center justify-center gap-2 self-start"
        >
          <Plus size={16} />
          Create Template
        </button>
      </div>

      {/* Search bar */}
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Search templates..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="form-input pl-9 w-full"
        />
      </div>

      {/* Templates grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {isLoading ? (
          <div className="col-span-full py-12 text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : filteredTemplates.map((tpl: any) => {
          const tags = extractMergeTags((tpl.htmlContent || '') + ' ' + (tpl.subject || ''));
          return (
            <div key={tpl.id} className="glass-panel rounded-2xl flex flex-col overflow-hidden group hover:shadow-lg hover:shadow-primary-500/5 transition-all duration-200">
              {/* Mini HTML preview strip */}
              <div className="h-28 bg-white dark:bg-slate-950 border-b border-slate-200/60 dark:border-white/5 overflow-hidden relative pointer-events-none select-none">
                <iframe
                  title={tpl.name}
                  srcDoc={tpl.htmlContent || '<p style="color:#94a3b8;font-family:sans-serif;padding:16px;font-size:12px;">No HTML content</p>'}
                  sandbox="allow-same-origin"
                  className="w-full border-none bg-white"
                  style={{ height: '400px', transform: 'scale(0.35)', transformOrigin: 'top left', width: '285%', pointerEvents: 'none' }}
                />
                {/* Gradient fade at the bottom */}
                <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
              </div>

              {/* Card body */}
              <div className="p-4 flex flex-col flex-1 gap-3">
                {/* Name + campaign count */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-primary-500/10 flex items-center justify-center text-primary-500 shrink-0">
                      <FileCode size={14} />
                    </div>
                    <h3 className="font-bold text-slate-800 dark:text-white text-sm truncate">{tpl.name}</h3>
                  </div>
                  {tpl.campaignCount > 0 && (
                    <span className="flex items-center gap-1 text-[10px] font-semibold text-primary-500 bg-primary-500/10 px-2 py-0.5 rounded-full shrink-0 border border-primary-500/20">
                      <Send size={10} />
                      {tpl.campaignCount}
                    </span>
                  )}
                </div>

                {/* Subject */}
                <div>
                  <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">Subject</div>
                  <p className="text-xs text-slate-600 dark:text-slate-300 truncate">{tpl.subject}</p>
                </div>

                {/* Merge tags — detected from HTML */}
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {tags.map(tag => (
                      <span key={tag} className="flex items-center gap-1 text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded font-mono">
                        <Tag size={9} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between border-t border-slate-200/60 dark:border-white/5 pt-3 mt-auto">
                  <span className="text-[10px] text-slate-400">
                    {new Date(tpl.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                  <div className="flex gap-0.5">
                    <button onClick={() => handlePreviewClick(tpl.id)} className="text-slate-400 hover:text-slate-800 dark:hover:text-white p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="Preview">
                      <Eye size={15} />
                    </button>
                    <button onClick={() => handleEditClick(tpl)} className="text-slate-400 hover:text-slate-800 dark:hover:text-white p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="Edit">
                      <Edit3 size={15} />
                    </button>
                    <button onClick={() => cloneMutation.mutate(tpl.id)} className="text-slate-400 hover:text-primary-500 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="Duplicate">
                      <Copy size={15} />
                    </button>
                    {user?.role === 'ADMIN' && (
                      <button onClick={() => setDeleteTargetId(tpl.id)} className="text-slate-400 hover:text-accent-rose p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors" title="Delete">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {!isLoading && filteredTemplates.length === 0 && (
          <div className="col-span-full py-16 text-center text-slate-400">
            {search ? `No templates matching "${search}"` : 'No templates yet. Click "Create Template" to get started.'}
          </div>
        )}
      </div>

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={!!deleteTargetId}
        title="Delete Template"
        message="This template will be permanently deleted. Any campaigns using it will retain their copy."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => deleteTargetId && deleteMutation.mutate(deleteTargetId)}
        onCancel={() => setDeleteTargetId(null)}
      />

      {/* Design Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={isEditMode ? 'Edit Template' : 'Create Template'}
        size="xl"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Template Name */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Template Name</label>
              <input
                type="text"
                placeholder="Monthly Newsletter"
                {...register('name')}
                className="form-input"
              />
              {errors.name && <span className="text-xs text-accent-rose">{errors.name.message}</span>}
            </div>

            {/* Email Subject line */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Email Subject</label>
              <input
                type="text"
                placeholder="Hello {{firstName}}, look at our updates!"
                {...register('subject')}
                className="form-input"
              />
              {errors.subject && <span className="text-xs text-accent-rose">{errors.subject.message}</span>}
            </div>
          </div>

          {/* HTML body editor */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <Code size={14} className="text-primary-500" />
                HTML Template Body
              </label>
              <span className="text-[10px] text-slate-500">
                Wrap tags like <code>{"{{firstName}}"}</code>, <code>{"{{unsubscribe_url}}"}</code>.
              </span>
            </div>
            <textarea
              rows={10}
              placeholder="<html><body><h1>Hi {{firstName}}!</h1><p>Our news goes here.</p><br><a href='{{unsubscribe_url}}'>Unsubscribe</a></body></html>"
              {...register('htmlContent')}
              className="form-input font-mono text-xs leading-relaxed"
            />
            {errors.htmlContent && <span className="text-xs text-accent-rose">{errors.htmlContent.message}</span>}
          </div>

          {/* Text content fallback */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Plain Text Fallback (Optional)</label>
            <textarea
              rows={3}
              placeholder="Hi {{firstName}}, read our newsletter here. To unsubscribe, click: {{unsubscribe_url}}"
              {...register('textContent')}
              className="form-input text-xs"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="btn-primary"
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Preview Sandbox Modal */}
      <Modal
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewData(null);
        }}
        title="Email Template Preview"
        size="lg"
      >
        <div className="space-y-4">
          {loadingPreview ? (
            <div className="py-12 text-center">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <span className="text-xs text-slate-500 block mt-3">Compiling merge values...</span>
            </div>
          ) : previewData ? (
            <div className="space-y-4">
              <div>
                <span className="text-[10px] font-bold text-slate-500 uppercase block">Subject line</span>
                <div className="text-sm text-slate-800 dark:text-slate-200 font-semibold p-3 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-lg">
                  {previewData.subject}
                </div>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Body Preview</span>
                <div className="w-full bg-white rounded-lg border border-white/10 overflow-hidden h-96">
                  {/* Secure Iframe sandbox for rendering preview code */}
                  <iframe
                    title="Template HTML Preview"
                    srcDoc={previewData.htmlContent}
                    sandbox="allow-same-origin"
                    className="w-full h-full border-none bg-white text-black"
                  />
                </div>
              </div>
            </div>
          ) : (
            <p className="text-slate-400">Failed to render template.</p>
          )}
        </div>
      </Modal>
    </div>
  );
};
export default Templates;
